/**
 * Pagina lista fornitori del modulo fornitori.
 *
 * Mostra i fornitori del tenant corrente con filtri per stato e ricerca.
 * Arricchita con conteggi catalogo e rappresentanti.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '@lib/useUrlState';

interface Fornitore {
  id: string;
  ragione_sociale: string | null;
  nome: string | null;
  cognome: string | null;
  partita_iva: string | null;
  email: string | null;
  telefono: string | null;
  stato: string;
  catalogo_count: number;
  rappresentanti_count: number;
}

/**
 * Pagina lista fornitori con ricerca e filtro per stato.
 */
const FornitoriList: React.FC = () => {
  const navigate = useNavigate();

  const [fornitori, setFornitori] = useState<Fornitore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [q, setQ] = useUrlState('q', '');
  const [stato, setStato] = useUrlState('stato', '__all__');

  const caricaFornitori = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (stato !== '__all__') params.set('stato', stato);

      const qs = params.toString();
      const url = `/api/v1/fornitori${qs ? `?${qs}` : ''}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
        },
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      const data: Fornitore[] = await response.json();
      setFornitori(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [q, stato]);

  useEffect(() => {
    void caricaFornitori();
  }, [caricaFornitori]);

  /** Restituisce il nome visualizzabile del fornitore. */
  const displayName = (f: Fornitore): string => {
    if (f.ragione_sociale) return f.ragione_sociale;
    if (f.cognome && f.nome) return `${f.cognome} ${f.nome}`;
    return f.cognome ?? f.nome ?? '—';
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          Fornitori
        </h1>
        <Button onClick={() => navigate('/fornitori/nuovo')}>
          Nuovo fornitore
        </Button>
      </div>

      {/* Filtri */}
      <Card>
        <CardHeader>
          <CardTitle>Filtri</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4">
          <Input
            placeholder="Cerca per nome, ragione sociale, CF…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-72"
          />

          <Select value={stato} onValueChange={setStato}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti</SelectItem>
              <SelectItem value="attivo">Attivo</SelectItem>
              <SelectItem value="disattivato">Disattivato</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Tabella */}
      <Card>
        <CardContent className="p-0">
          {loading && (
            <p className="p-6 text-center text-sm text-muted-foreground">
              Caricamento…
            </p>
          )}
          {error && (
            <p className="p-6 text-center text-sm text-destructive">{error}</p>
          )}
          {!loading && !error && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome / Ragione sociale</TableHead>
                  <TableHead>P.IVA</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Telefono</TableHead>
                  <TableHead className="text-center">Catalogo</TableHead>
                  <TableHead className="text-center">Rappresentanti</TableHead>
                  <TableHead>Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fornitori.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center text-sm text-muted-foreground"
                    >
                      Nessun fornitore trovato
                    </TableCell>
                  </TableRow>
                )}
                {fornitori.map((fornitore: Fornitore) => (
                  <TableRow
                    key={fornitore.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/fornitori/${fornitore.id}`)}
                  >
                    <TableCell className="font-medium">
                      {displayName(fornitore)}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {fornitore.partita_iva ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {fornitore.email ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {fornitore.telefono ?? '—'}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">
                        {fornitore.catalogo_count}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">
                        {fornitore.rappresentanti_count}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          fornitore.stato === 'attivo' ? 'default' : 'secondary'
                        }
                      >
                        {fornitore.stato === 'attivo' ? 'Attivo' : 'Disattivato'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FornitoriList;
